import React, {useState, useEffect} from 'react';
import {Box, TextField, Typography} from '@mui/material';
import {CustomButton, Select} from '..';
import {useFormInput} from '../../utils/utilities';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {addSupervisor, editSupervisor, editProfile} from '../../actions';
import {fieldError, isFieldIvalid} from '../../utils/errorHandler';

const formatArray = array => {
  return array.map(obj => ({id: obj.id, name: obj.name}));
};

const SupervisorModal = ({supervisorToEdit, profile}) => {
  const {allElevators} = useSelector(s => s.elevators);
  const {user, authLoading, authError} = useSelector(s => s.user);
  const {supervisorsLoading, supervisorsError} = useSelector(s => s.supervisors);
  const firstName = useFormInput(
    profile ? user.firstName : supervisorToEdit ? supervisorToEdit.firstName : '',
  );
  const lastName = useFormInput(
    profile ? user.lastName : supervisorToEdit ? supervisorToEdit.lastName : '',
  );
  const email = useFormInput(
    profile ? user.email : supervisorToEdit ? supervisorToEdit.email : '',
  );
  const phoneNumber = useFormInput(
    profile ? user.phoneNumber : supervisorToEdit ? supervisorToEdit.phoneNumber : '',
  );
  const address = useFormInput(
    profile ? user.address : supervisorToEdit ? supervisorToEdit.address : '',
  );
  const [formattedElevators, setFormattedElevators] = useState([]);
  const birthday = useFormInput(
    profile
      ? moment(profile.birthday).format('yyyy-MM-DD')
      : supervisorToEdit
      ? moment(supervisorToEdit.birthday).format('yyyy-MM-DD')
      : moment('1990-01-01').format('yyyy-MM-DD'),
  );

  const [elevators, setElevators] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    setFormattedElevators(formatArray(allElevators));
  }, []);

  useEffect(() => {
    if (formattedElevators.length > 0 && supervisorToEdit && supervisorToEdit.elevators) {
      const values = supervisorToEdit.elevators.map(obj => obj.id);
      setElevators(typeof values === 'string' ? values.split(',') : values);
    }
  }, [formattedElevators]);

  useEffect(() => {
    if (supervisorToEdit) {
      firstName.manuallyChange(supervisorToEdit.firstName);
      lastName.manuallyChange(supervisorToEdit.lastName);
      email.manuallyChange(supervisorToEdit.email);
      phoneNumber.manuallyChange(supervisorToEdit.phoneNumber);
      address.manuallyChange(supervisorToEdit.address);
      birthday.manuallyChange(
        moment(supervisorToEdit.birthday).format('yyyy-MM-DD'),
      );
    }
  }, [supervisorToEdit]);

  const onElevatorsChange = value => {
    setElevators(value);
  };

  const onSubmit = e => {
    e.preventDefault();
    const birthdayMs = moment(birthday.value).add(1, 'day').valueOf();
    const values = [
      email.value,
      firstName.value,
      lastName.value,
      phoneNumber.value,
      address.value,
      birthdayMs,
    ];
    if (supervisorToEdit) {
      dispatch(editSupervisor(...values, elevators, supervisorToEdit.id));
    } else if (profile) {
      values.shift();
      dispatch(editProfile(...values));
    } else {
      console.log("Elevators selected: ", elevators);
      dispatch(addSupervisor(...values, elevators));
    }
  };

  return (
    <>
      <Typography alignSelf="center" variant="h6" mt="1rem" fontWeight="bold">
        {!supervisorToEdit && !profile && 'Add a new elevator supervisor'}
      </Typography>
      <form onSubmit={onSubmit}>
        <Box display="flex" justifyContent="center">
          <Box
            display="flex"
            flexDirection="column"
            mt="2rem"
            gap="1rem"
            sx={{
              width: {
                xs: 'calc(100vw - 4rem)',
                sm: supervisorToEdit ? '70%' : '30rem',
              },
            }}>
            <Box display="flex" gap="1rem">
              <TextField
                {...firstName}
                error={isFieldIvalid(
                  profile ? authError : supervisorsError,
                  'firstName',
                )}
                helperText={fieldError(
                  profile ? authError : supervisorsError,
                  'firstName',
                )}
                required
                label="First name"
                fullWidth
              />
              <TextField
                {...lastName}
                error={isFieldIvalid(
                  profile ? authError : supervisorsError,
                  'lastName',
                )}
                helperText={fieldError(
                  profile ? authError : supervisorsError,
                  'lastName',
                )}
                required
                label="Last name"
                fullWidth
              />
            </Box>
            <TextField
              {...email}
              disabled={profile}
              error={isFieldIvalid(profile ? authError : supervisorsError, 'email')}
              helperText={fieldError(
                profile ? authError : supervisorsError,
                'email',
              )}
              required
              label="Email"
            />
            <Box display="flex" gap="1rem">
              <TextField
                {...phoneNumber}
                error={isFieldIvalid(
                  profile ? authError : supervisorsError,
                  'phoneNumber',
                )}
                helperText={fieldError(
                  profile ? authError : supervisorsError,
                  'phoneNumber',
                )}
                required
                label="Phone number"
                fullWidth
              />
              <TextField
                type={'date'}
                {...birthday}
                error={isFieldIvalid(
                  profile ? authError : supervisorsError,
                  'birthday',
                )}
                helperText={fieldError(
                  profile ? authError : supervisorsError,
                  'birthday',
                )}
                required
                label="Birthday"
                fullWidth
              />
            </Box>
            <TextField
              {...address}
              error={isFieldIvalid(
                profile ? authError : supervisorsError,
                'address',
              )}
              helperText={fieldError(
                profile ? authError : supervisorsError,
                'address',
              )}
              required
              label="Address"
            />
            {!profile && (
              <Select
                value={elevators}
                onChangeValue={onElevatorsChange}
                items={formattedElevators}
                label="Assign elevators"
                multiple
                error={isFieldIvalid(supervisorsError, 'elevatorIds')}
                helperText={fieldError(supervisorsError, 'elevatorIds')}
              />
            )}
            <CustomButton loading={supervisorsLoading || authLoading} type="submit">
              {supervisorToEdit || profile ? 'Save' : 'Submit'}
            </CustomButton>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default SupervisorModal;
